// app/services/settings.service.ts
import {inject, Injectable, InjectionToken, signal} from '@angular/core';
import {BehaviorSubject, filter, map} from 'rxjs';
import {PublicSettings} from '@gatewee/types/system';
export const publicSettings = new InjectionToken<PublicSettings>(
  'PUBLIC_SETTINGS',
);
@Injectable({providedIn: 'root'})
export class SettingsService {
  private readonly initial = inject(publicSettings);
  private readonly _cfg = signal<PublicSettings>(this.initial);
  private readonly _settingsSubject =
    new BehaviorSubject<PublicSettings | null>(null);

  // Observable stream for reactive programming
  public readonly settings$ = this._settingsSubject.asObservable().pipe(
    filter(settings => settings !== null),
    map(settings => settings!),
  );

  constructor() {
    this._updateObservableStream();
  }

  // ===== GENERIC GET METHODS =====

  /**
   * Generic method to get business rules or branding (synchronous)
   *
   * @example
   * // Get business rule
   * const paymentMethods = this.settingsService.get('businessRules', 'paymentMethods');
   *
   * @example
   * // Get branding
   * const pageBranding = this.settingsService.get('branding', 'page');
   *
   * @example
   * // Get entire section
   * const branding = this.settingsService.get('branding');
   */
  get<T extends keyof PublicSettings>(section: T): PublicSettings[T];
  get<T extends keyof PublicSettings, K extends keyof PublicSettings[T]>(
    section: T,
    key: K,
  ): PublicSettings[T][K];
  get<T extends keyof PublicSettings, K extends keyof PublicSettings[T]>(
    section: T,
    key?: K,
  ): PublicSettings[T] | PublicSettings[T][K] {
    const settings = this._cfg();

    if (key !== undefined) {
      return settings?.[section]?.[key] as PublicSettings[T][K];
    }

    return settings?.[section] ?? ({} as PublicSettings[T]);
  }

  /**
   * Generic method to get business rules or branding (async)
   *
   * @example
   * const paymentMethods = await this.settingsService.getAsync('businessRules', 'paymentMethods');
   */
  async getAsync<T extends keyof PublicSettings>(
    section: T,
    key: keyof PublicSettings[T],
  ): Promise<PublicSettings[T][keyof PublicSettings[T]]> {
    const settings = await this.waitForSettings();
    return settings[section][key];
  }

  getAuthorized(): boolean {
    const settings = this._cfg();
    return settings?.authorized ?? false;
  }

  // ===== UTILITY METHODS =====

  private _updateObservableStream(): void {
    const settings = this._cfg();
    if (settings) {
      this._settingsSubject.next(settings);
    }
  }

  /** Verifica se as configurações estão carregadas */
  isSettingsLoaded(): boolean {
    const settings = this._cfg();
    return !!(settings && settings.businessRules && settings.branding);
  }

  /** Aguarda o carregamento das configurações */
  async waitForSettings(): Promise<PublicSettings> {
    return new Promise(resolve => {
      if (this.isSettingsLoaded()) {
        const settings = this._cfg();
        this._updateObservableStream();
        resolve(settings);
        return;
      }

      // Polling para verificar se as configurações foram carregadas
      const interval = setInterval(() => {
        if (this.isSettingsLoaded()) {
          clearInterval(interval);
          const settings = this._cfg();
          this._updateObservableStream();
          resolve(settings);
        }
      }, 100);

      // Timeout de segurança (5 segundos)
      setTimeout(() => {
        clearInterval(interval);
        const settings = this._cfg();
        this._updateObservableStream();
        resolve(settings);
      }, 5000);
    });
  }

  /** Limpa memória e localStorage */
  clear() {
    this._cfg.set({
      branding: {},
      businessRules: {},
    } as PublicSettings);

    try {
      localStorage.removeItem('businessRules');
      localStorage.removeItem('branding');
    } catch {
      // ignore
    }
  }
}
